/** @jsx jsx */
import { Flex, FormGroup, FormLabel } from "@eworkit/iris";
import { FormikProps } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";
import * as Yup from "yup";

import { FormikFileUpload } from "@/components/Files";
import { FileUploadStateItem } from "@/components/Files/upload/fileUploadReducer";
import { FormikTextArea, VALIDATION_TYPES } from "@/components/Formik";
import { FormikError } from "@/components/Formik";
import { Attachment } from "@/data/attachments";

const COMMENT_MAX_LENGTH = 1000;

export interface TimeReportSpecificationFormValues {
  comment?: string;
  attachments: FileUploadStateItem<Attachment>[];
}

export const TIME_REPORT_SPECIFICATION_FORM_DEFAULT_VALUES: TimeReportSpecificationFormValues = {
  attachments: [],
};

export const TIME_REPORT_SPECIFICATION_VALIDATION_SCHEMA = Yup.object().shape({
  comment: Yup.string().max(COMMENT_MAX_LENGTH, VALIDATION_TYPES.MAX_LENGTH),
  attachments: Yup.array()
    .test({
      message: VALIDATION_TYPES.UPLOAD_PROGRESS,
      test: (value: FileUploadStateItem<Attachment>[]) => value.every(i => i.progress === 100),
    })
    .test({
      message: VALIDATION_TYPES.UPLOAD_ERRORS,
      test: (value: FileUploadStateItem<Attachment>[]) => value.every(i => !i.error),
    }),
});

interface TimeReportSpecificationFormFieldsProps {
  formik: FormikProps<TimeReportSpecificationFormValues>;
}

export const TimeReportSpecificationFormFields: FC<TimeReportSpecificationFormFieldsProps> = ({ formik }) => {
  const { t } = useTranslation("session");

  return (
    <Flex flexDirection="column" gap="sm">
      <FormGroup
        label={<FormLabel label={t("TIME_REPORTS.SPECIFICATION.COMMENT")} />}
        error={
          <FormikError
            name="comment"
            overrideMessages={{
              [VALIDATION_TYPES.MAX_LENGTH]: t("common:VALIDATION_MESSAGES.MAX_LENGTH", {
                value: COMMENT_MAX_LENGTH,
              }),
            }}
          />
        }>
        <FormikTextArea name="comment" placeholder={t("TIME_REPORTS.SPECIFICATION.COMMENT_PLACEHOLDER")} rows={3} />
      </FormGroup>

      <FormGroup label={<FormLabel label={t("TIME_REPORTS.SPECIFICATION.ATTACHMENTS")} />}>
        <FormikFileUpload
          name="attachments"
          attachments={formik.values.attachments.filter(i => i.attachment).map(i => i.attachment!)}
        />
      </FormGroup>
    </Flex>
  );
};
